// =============================================================================
// AccountMenu — the Dashboard header's account button. It replaced the bare
// "Sign out" button and holds two things: Appearance (Dark / Light, per
// device) and Sign out. Same menu pattern as MoreMenu (aria-haspopup, role=menu,
// focus moves in on open, Up/Down, Escape or an outside tap closes, 44px rows).
//
// Not MoreMenu itself: MoreMenu takes a list of plain action rows, and this
// menu also holds the two-option Appearance control. Unlike MoreMenu it does NOT
// carry data-theme="dark": MoreMenu is a dark island on a page that stays dark,
// but this menu must follow the theme so it reads in light mode like the rest.
// =============================================================================
import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react'
import { LogOut, UserRound } from 'lucide-react'
import AppearanceRow from '@/components/AppearanceRow'

const ITEMS = '[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled)'

export default function AccountMenu({ onSignOut }: { onSignOut: () => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()
  // Close and hand focus back to the button that opened the menu, so a keyboard
  // user does not land on <body> when the focused row unmounts.
  const closeToTrigger = useCallback(() => { setOpen(false); triggerRef.current?.focus() }, [])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') closeToTrigger() }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    ref.current?.querySelector<HTMLElement>(ITEMS)?.focus()
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, closeToTrigger])

  function onMenuKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const rows = Array.from(ref.current?.querySelectorAll<HTMLElement>(ITEMS) ?? [])
    const at = rows.indexOf(document.activeElement as HTMLElement)
    const next = e.key === 'ArrowDown' ? (at + 1) % rows.length : (at - 1 + rows.length) % rows.length
    rows[next]?.focus()
  }

  // Tabbing out of the menu closes it; focus is already moving on, so do not pull it back.
  function onMenuBlur(e: FocusEvent<HTMLDivElement>) {
    const to = e.relatedTarget as Node | null
    if (open && to && ref.current && !ref.current.contains(to)) setOpen(false)
  }

  return (
    <div ref={ref} className="relative" onBlur={onMenuBlur}>
      <button ref={triggerRef} type="button" aria-label="Account" title="Account" aria-haspopup="menu" aria-expanded={open}
        aria-controls={open ? menuId : undefined} onClick={() => setOpen(o => !o)}
        className="btn-ghost btn-sm">
        <UserRound size={16} aria-hidden="true" />
        <span className="hidden md:inline">Account</span>
      </button>
      {open && (
        <div id={menuId} role="menu" aria-label="Account" tabIndex={-1} onKeyDown={onMenuKey}
          className="absolute right-0 mt-1 z-dropdown motion-safe:animate-menu-in origin-top-right min-w-[15rem] max-w-[calc(100vw-2rem)] surface border hairline-strong rounded-xl shadow-2xl py-1">
          <AppearanceRow onChosen={closeToTrigger} />
          <button type="button" role="menuitem"
            onClick={() => { closeToTrigger(); onSignOut() }}
            className="w-full min-h-[44px] px-3 text-left text-sm text-fg hover:bg-raised/40 focus:bg-raised outline-none inline-flex items-center gap-2">
            <LogOut size={16} className="text-fg-muted shrink-0" aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
